import { TimePicker } from 'antd';
import { RangePickerProps } from 'antd/es/date-picker';
import { DatePickerIcon } from 'assets/icons';
import { Control, FieldValues, Path, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

const { RangePicker } = TimePicker;

interface StyledWrapperProps {
  directionLabel: 'horizontal' | 'vertical';
}

export type RangeTimePickerFieldProps<T extends FieldValues> = RangePickerProps & {
  name: Path<T>;
  control: Control<T>;
  directionLabel?: 'horizontal' | 'vertical';
  label?: string;
};

const StyledContainer = styled.div`
  .ant-picker {
    border-radius: 6px;
    border: 1px solid var(--cl_neutral300);
  }
`;

const StyledWrapper = styled.div`
  ${(props: StyledWrapperProps) =>
    props?.directionLabel === 'horizontal' &&
    css`
      display: flex;
      align-items: center;
    `}
`;

const StyledLabel = styled.p`
  display: flex;
  align-items: flex-start;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: var(--cl_gray900);
  ${(props: StyledWrapperProps) =>
    props?.directionLabel === 'vertical' &&
    css`
      margin-bottom: 6px;
    `}
`;

const StyledErrorMessage = styled.p`
  color: var(--cl_error500);
  margin-top: 6px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
`;

export function RangeTimePickerField<T extends FieldValues>({
  name,
  control,
  directionLabel = 'vertical',
  label,
  onChange: externalOnChange,
  ...rest
}: RangeTimePickerFieldProps<T>) {
  const {
    field: { onChange, value, ref },
    fieldState: { error },
  } = useController({
    name,
    control,
  });
  const { t } = useTranslation(['recruitment']);

  return (
    <StyledContainer>
      <StyledWrapper directionLabel={directionLabel}>
        {label && <StyledLabel directionLabel={directionLabel}>{label}</StyledLabel>}

        <RangePicker
          status={error?.message ? 'error' : undefined}
          ref={ref}
          value={value}
          suffixIcon={<DatePickerIcon />}
          placeholder={[t('recruitment:start_time'), t('recruitment:end_time')]}
          onChange={(times, timeStrings) => {
            onChange(times);
            externalOnChange?.(times, timeStrings);
          }}
          {...rest}
        />
      </StyledWrapper>

      {error?.message && <StyledErrorMessage>{error?.message}</StyledErrorMessage>}
    </StyledContainer>
  );
}
